import React from 'react';
import { Link } from 'react-router-dom';
import { Listing } from '../types';
import { getListingPhoto, getRatingLabel, getRatingDisplay } from '../services/listingHelpers';
import StarRating from './StarRating';
import CompareToggle from './CompareToggle';
import RatingProvenance from './RatingProvenance';
import { trackAnalyticsEvent } from '../services/analytics';

interface Props {
  listing: Listing;
}

const listingInitialsSvg = (name: string): string => {
  const words = (name || '').trim().split(/\s+/).filter(Boolean);
  const initials = words.length >= 2
    ? (words[0][0] + words[1][0]).toUpperCase()
    : ((words[0] || '?')[0] || '?').toUpperCase();
  return `data:image/svg+xml,${encodeURIComponent(
    `<svg xmlns="http://www.w3.org/2000/svg" width="400" height="200">` +
    `<defs><linearGradient id="g" x1="0" y1="0" x2="1" y2="1">` +
    `<stop offset="0%" stop-color="#1a237e"/>` +
    `<stop offset="100%" stop-color="#3949ab"/>` +
    `</linearGradient></defs>` +
    `<rect width="400" height="200" fill="url(#g)"/>` +
    `<text x="50%" y="50%" font-family="sans-serif" font-size="64" font-weight="bold" ` +
    `fill="rgba(255,255,255,0.9)" text-anchor="middle" dy=".35em">${initials}</text>` +
    `</svg>`
  )}`;
};

const ListingCard: React.FC<Props> = ({ listing }) => {
  const photo = getListingPhoto(listing) || listingInitialsSvg(listing.name);
  const display = getRatingDisplay(listing);
  const rating = display.headlineValue;
  const reviewCount = display.headlineCount;
  const ratingLabel = getRatingLabel(rating);
  const primaryPhone = listing.phones && listing.phones.length > 0 ? listing.phones[0] : null;
  const listingTypeLabel = listing.listingType ? listing.listingType.join(', ') : '';

  return (
    <div style={{
      border: '1px solid #e0e0e0',
      borderRadius: '8px',
      overflow: 'hidden',
      background: '#fff',
      display: 'flex',
      flexDirection: 'column',
      boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
      transition: 'box-shadow 0.2s',
    }}>
      <div style={{ position: 'relative', height: '180px', overflow: 'hidden', background: '#f0f0f0' }}>
        <img src={photo} alt={listing.name}
          style={{ width: '100%', height: '100%', objectFit: 'cover' }}
          onError={(e) => {
            const img = e.target as HTMLImageElement;
            const fallback = listingInitialsSvg(listing.name);
            if (img.src !== fallback) img.src = fallback;
          }}
        />
        {listing.vipSubscriptions?.some(v => v.status === 'active') && (
          <span style={{
            position: 'absolute', top: '8px', left: '8px',
            background: '#f5a623', color: '#fff', padding: '2px 8px',
            borderRadius: '4px', fontSize: '11px', fontWeight: 700
          }}>VIP</span>
        )}
        {listing.inAdRotation && (
          <span style={{
            position: 'absolute', top: '8px', right: '8px',
            background: '#2196F3', color: '#fff', padding: '2px 8px',
            borderRadius: '4px', fontSize: '11px', fontWeight: 700
          }}>Sponsored</span>
        )}
      </div>

      <div style={{ padding: '16px', flex: 1, display: 'flex', flexDirection: 'column' }}>
        <Link to={`/listing/${listing.listingId}`}
          style={{ textDecoration: 'none', color: '#1a237e', fontWeight: 700, fontSize: '18px', marginBottom: '4px', display: 'block' }}>
          {listing.name}
        </Link>

        {listing.address && (
          <p style={{ margin: '0 0 8px', color: '#666', fontSize: '14px' }}>
            📍 {listing.address.city}, {listing.address.region}
          </p>
        )}

        {listingTypeLabel && (
          <p style={{ margin: '0 0 8px', fontSize: '13px', color: '#888' }}>
            {listingTypeLabel}
          </p>
        )}

        <div style={{ marginBottom: '8px' }}>
          <StarRating rating={rating} size="sm" />
          {reviewCount > 0 && (
            <span style={{ fontSize: '12px', color: '#888', marginLeft: '4px' }}>
              ({reviewCount} reviews)
            </span>
          )}
          {ratingLabel && (
            <span style={{ fontSize: '12px', color: '#4caf50', fontWeight: 600, marginLeft: '4px' }}>
              · {ratingLabel}
            </span>
          )}
          {display.headlineSource === 'historical' && (
            <div><RatingProvenance count={display.historicalCount} compact /></div>
          )}
          {display.headlineSource === 'first-party' && reviewCount > 0 && (
            <div style={{ fontSize: '11px', color: '#2e7d32' }}>✓ Verified TopRatedRehabs reviews</div>
          )}
        </div>

        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px', marginBottom: '12px' }}>
          {listing.treatmentTypes?.slice(0, 3).map((t, i) => (
            <span key={i} style={{
              background: '#e3f2fd', color: '#1565c0', padding: '2px 8px',
              borderRadius: '12px', fontSize: '11px'
            }}>{t}</span>
          ))}
          {listing.insuranceAccepted && listing.insuranceAccepted.length > 0 && (
            <span style={{
              background: '#e8f5e9', color: '#2e7d32', padding: '2px 8px',
              borderRadius: '12px', fontSize: '11px'
            }}>Insurance Accepted</span>
          )}
          {listing.financingAvailable && (
            <span style={{
              background: '#fff3e0', color: '#e65100', padding: '2px 8px',
              borderRadius: '12px', fontSize: '11px'
            }}>Financing</span>
          )}
        </div>

        <div style={{ display: 'flex', gap: '8px', marginTop: 'auto', alignItems: 'center' }}>
          <Link to={`/listing/${listing.listingId}`} style={{
            flex: 1, textAlign: 'center', padding: '8px',
            background: '#1a237e', color: '#fff', textDecoration: 'none',
            borderRadius: '6px', fontSize: '14px', fontWeight: 600
          }}>View Details</Link>
          {primaryPhone && (
            <a href={`tel:${primaryPhone.telLink || primaryPhone.display}`}
              onClick={() => trackAnalyticsEvent('phone_click', { placement: 'listing_card' })} style={{
              flex: 1, textAlign: 'center', padding: '8px',
              background: '#43a047', color: '#fff', textDecoration: 'none',
              borderRadius: '6px', fontSize: '14px', fontWeight: 600
            }}>📞 Call</a>
          )}
          <CompareToggle listingId={listing.listingId} listingName={listing.name} />
        </div>
      </div>
    </div>
  );
};

export default ListingCard;
